import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';

/**
 * D235. A control that cannot be used looks it, and says why when pressed.
 *
 * A native `disabled` button fires no click, so it can never say why. Every disabled
 * control in the app is marked `aria-disabled` instead, with its D180 reason on
 * `data-off-reason`. One listener, installed by `OffNoteHost`, catches the click on any of
 * them before the control's own handler sees it, cancels it, and shows the reason in a
 * small note beside the control. The action itself can never run: the click stops at the
 * document, and its default (a form submit) is cancelled. The look is one rule in
 * `base.css`, keyed on the same attribute, so every screen picks it up.
 */

/** How long the note stays before it closes on its own. */
export const OFF_NOTE_MS = 4000;

/** The attributes that mark a control disabled, with the reason it gives when pressed. */
export interface OffAttrs {
  'aria-disabled'?: 'true';
  'data-off-reason'?: string;
}

/** Mark a control disabled (D235), carrying the reason it gives when pressed (D180). */
export function offProps(off: boolean, reason: string | undefined): OffAttrs {
  return off ? { 'aria-disabled': 'true', 'data-off-reason': reason ?? '' } : {};
}

/**
 * A reason drawn inside a disabled control keeps its own ink unstruck. A line-through
 * reaches every block inside the control, but not an inline block, so the reason is one.
 */
export const UNSTRUCK: JSX.CSSProperties = { display: 'inline-block', textDecoration: 'none' };

interface Shown {
  text: string;
  top: number;
  left: number;
  above: boolean;
}

/** The note is at most this wide, and kept this far inside the screen's edges. */
const NOTE_W = 240;
const EDGE = 8;

function place(el: Element, text: string): Shown {
  const r = el.getBoundingClientRect();
  const w = window.innerWidth;
  const h = window.innerHeight;
  const left = Math.max(EDGE, Math.min(r.left, w - NOTE_W - EDGE));
  // Beside the control: under it, or over it when it sits near the foot of the screen.
  const above = r.bottom + 60 > h;
  return { text, left, above, top: above ? r.top - 6 : r.bottom + 6 };
}

/**
 * Catches a press on any disabled control and shows its reason. Mounted once, beside the
 * app. The reason is also put in a live region, so a screen reader says it.
 */
export function OffNoteHost() {
  const [shown, setShown] = useState<Shown | null>(null);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let openedAt = 0;
    const close = () => {
      clearTimeout(timer);
      setShown(null);
    };
    const onClick = (e: MouseEvent) => {
      const t = e.target;
      const el = t instanceof Element ? t.closest('[aria-disabled="true"]') : null;
      if (!el) return;
      // The action never runs: the control's own handler is below this one, and a submit
      // button's default is the submit.
      e.preventDefault();
      e.stopPropagation();
      const text = el.getAttribute('data-off-reason') ?? '';
      clearTimeout(timer);
      if (!text) {
        setShown(null);
        return;
      }
      setShown(place(el, text));
      openedAt = performance.now();
      timer = setTimeout(close, OFF_NOTE_MS);
    };
    // The next tap, or key, closes it. A tap on a disabled control opens its own note
    // again straight after, from the click that follows.
    const onDown = () => close();
    // A scroll moves the control away from its note. One that lands just after the press
    // is the browser bringing the control into view for it, so it is let be.
    const onScroll = () => {
      if (performance.now() - openedAt > 300) close();
    };
    document.addEventListener('click', onClick, true);
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onDown);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onDown);
    };
  }, []);

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
        }}
      >
        {shown?.text ?? ''}
      </div>
      {shown && (
        <div
          data-testid="off-note"
          aria-hidden="true"
          style={{
            position: 'fixed',
            left: shown.left,
            top: shown.top,
            transform: shown.above ? 'translateY(-100%)' : undefined,
            maxWidth: NOTE_W,
            zIndex: 1000,
            padding: '6px 10px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--ink)',
            color: 'var(--bg)',
            font: '400 12px/1.35 var(--font-ui)',
            pointerEvents: 'none',
          }}
        >
          {shown.text}
        </div>
      )}
    </>
  );
}
